/**
 * Gift Options Cart Component
 * Handles the "Send as Gift" feature with toggle behavior, character counting,
 * and gift wrap product management (add/remove from cart)
 */

if (!customElements.get('gift-options-cart')) {
  customElements.define(
    'gift-options-cart',
    class GiftOptionsCart extends HTMLElement {
      constructor() {
        super();
        this.formId = this.dataset.formId;
        this.giftWrapVariantId = this.dataset.giftWrapVariantId;

        // Translated labels for line item properties (from data attributes)
        this.labelRecipientName = this.dataset.labelRecipientName || 'Recipient Name';
        this.labelRecipientPhone = this.dataset.labelRecipientPhone || 'Recipient Phone';
        this.labelGiftMessage = this.dataset.labelGiftMessage || 'Gift Message';

        // Find all inputs based on their name attributes
        this.toggleCheckbox = this.querySelector('input[name="attributes[Is Gift]"][type="checkbox"]');
        this.giftWrapCheckbox = this.querySelector('input[name="attributes[Gift Wrap]"][type="checkbox"]');
        this.recipientNameInput = this.querySelector('input[name="attributes[Recipient Name]"]');
        this.recipientPhoneInput = this.querySelector('input[name="attributes[Recipient Phone]"]');
        this.giftMessageTextarea = this.querySelector('textarea[name="attributes[Gift Message]"]');

        // Fields container
        this.fieldsContainer = this.querySelector('.gift-options-fields');

        // Character counter
        this.characterCounter = this.querySelector('.character-counter');
        this.maxChars = this.giftMessageTextarea?.maxLength || 200;

        // State tracking
        this.isUpdating = false;

        this.init();
      }

      init() {
        if (!this.toggleCheckbox) return;

        // Enable the checkbox (might be disabled by default for no-JS fallback)
        this.toggleCheckbox.disabled = false;

        // Set up event listeners
        this.toggleCheckbox.addEventListener('change', this.onToggleChange.bind(this));

        if (this.giftWrapCheckbox) {
          this.giftWrapCheckbox.addEventListener('change', this.onGiftWrapChange.bind(this));
        }

        if (this.giftMessageTextarea) {
          this.giftMessageTextarea.addEventListener('input', this.updateCharacterCounter.bind(this));
          // Save attributes when user finishes typing
          this.giftMessageTextarea.addEventListener('blur', this.saveCartAttributes.bind(this));
          // Initialize counter
          this.updateCharacterCounter();
        }

        // Save attributes when recipient fields change
        if (this.recipientNameInput) {
          this.recipientNameInput.addEventListener('blur', this.saveCartAttributes.bind(this));
        }
        if (this.recipientPhoneInput) {
          this.recipientPhoneInput.addEventListener('blur', this.saveCartAttributes.bind(this));
        }

        // Set initial state
        this.updateFieldsVisibility();
      }

      connectedCallback() {
        // Sync state with cart on initialization (drawer open)
        this.syncGiftWrapFromCart();

        // Subscribe to cart update events if PUB_SUB_EVENTS is available
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            // Sync gift wrap checkbox state after cart updates (unless we triggered the update)
            if (event.source !== 'gift-options-cart') {
              this.syncGiftWrapFromCart();
            }
          });
        }

        // Also listen for cart:refresh events
        this.boundSyncHandler = this.syncGiftWrapFromCart.bind(this);
        document.addEventListener('cart:refresh', this.boundSyncHandler);

        // Intercept checkout to add gift details to order notes
        this.boundCheckoutHandler = this.handleCheckout.bind(this);
        document.addEventListener('submit', this.boundCheckoutHandler, true);
        document.addEventListener('click', this.boundCheckoutClickHandler = this.handleCheckoutClick.bind(this), true);
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        // Clean up cart:refresh event listener
        if (this.boundSyncHandler) {
          document.removeEventListener('cart:refresh', this.boundSyncHandler);
        }
        // Clean up checkout interceptors
        if (this.boundCheckoutHandler) {
          document.removeEventListener('submit', this.boundCheckoutHandler, true);
        }
        if (this.boundCheckoutClickHandler) {
          document.removeEventListener('click', this.boundCheckoutClickHandler, true);
        }
      }

      onToggleChange() {
        this.updateFieldsVisibility();
        this.updateAriaAttributes();

        if (!this.toggleCheckbox.checked) {
          // Clear fields and remove gift wrap product when unchecking
          this.clearFields();
          if (this.giftWrapVariantId && this.giftWrapCheckbox?.checked) {
            this.removeGiftWrapFromCart();
          }
          // Clear cart attributes when unchecking
          this.clearCartAttributes();
        } else {
          // Save "Is Gift" attribute when checking
          this.saveCartAttributes();
        }
      }

      onGiftWrapChange() {
        // Save the gift wrap attribute
        this.saveCartAttributes();

        if (!this.giftWrapVariantId) return;

        if (this.giftWrapCheckbox.checked) {
          this.addGiftWrapToCart();
        } else {
          this.removeGiftWrapFromCart();
        }
      }

      /**
       * Handle checkout form submission
       */
      handleCheckout(event) {
        const form = event.target;
        if (!form || form.tagName !== 'FORM') return;

        // Check if this is a checkout form
        const isCheckoutForm = form.action?.includes('/checkout') ||
                               form.querySelector('button[name="checkout"]') ||
                               form.querySelector('input[name="checkout"]');

        if (!isCheckoutForm) return;

        // If not a gift order, let checkout proceed normally
        if (!this.toggleCheckbox?.checked) return;

        // Prevent default and add gift details to order note
        event.preventDefault();
        this.addGiftDetailsToOrderNote().then(() => {
          // Re-submit the form after updating note
          form.submit();
        });
      }

      /**
       * Handle checkout button clicks (for buttons outside forms)
       */
      handleCheckoutClick(event) {
        const button = event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
        if (!button) return;

        // If not a gift order, let checkout proceed normally
        if (!this.toggleCheckbox?.checked) return;

        // For links, we need to intercept differently
        if (button.tagName === 'A') {
          event.preventDefault();
          this.addGiftDetailsToOrderNote().then(() => {
            window.location.href = button.href;
          });
          return;
        }

        // For buttons inside forms, let handleCheckout handle it
        const form = button.closest('form');
        if (form) return;

        // Standalone button - shouldn't happen but handle it
        event.preventDefault();
        this.addGiftDetailsToOrderNote().then(() => {
          window.location.href = window.Shopify.routes.root + 'checkout';
        });
      }

      /**
       * Format and add gift details to order note AND line item properties
       */
      async addGiftDetailsToOrderNote() {
        // Skip if already processing
        if (this.isAddingNote) return;
        this.isAddingNote = true;

        try {
          const recipientName = this.recipientNameInput?.value?.trim() || '';
          const recipientPhone = this.recipientPhoneInput?.value?.trim() || '';
          const giftMessage = this.giftMessageTextarea?.value?.trim() || '';
          const hasGiftWrap = this.giftWrapCheckbox?.checked;

          // Build the gift note for order notes (visible in admin)
          let giftNote = '🎁 GIFT ORDER\n';
          giftNote += '━━━━━━━━━━━━━━━━━━━━\n';

          if (recipientName) {
            giftNote += `Recipient: ${recipientName}\n`;
          }

          if (recipientPhone) {
            giftNote += `Phone: ${recipientPhone}\n`;
          }

          if (giftMessage) {
            giftNote += `\nGift Message:\n"${giftMessage}"\n`;
          }

          if (hasGiftWrap) {
            giftNote += `\n✓ Gift Wrap Included\n`;
          }

          giftNote += '━━━━━━━━━━━━━━━━━━━━';

          // Update cart note
          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ note: giftNote }),
          });

          // If gift wrap is in cart, update it with visible line item properties
          if (hasGiftWrap && this.giftWrapVariantId) {
            await this.updateGiftWrapWithProperties(recipientName, recipientPhone, giftMessage);
          }

        } catch (error) {
          console.error('Error adding gift details to order note:', error);
        } finally {
          this.isAddingNote = false;
        }
      }

      /**
       * Update gift wrap line item with visible properties for checkout display
       */
      async updateGiftWrapWithProperties(recipientName, recipientPhone, giftMessage) {
        try {
          // First, remove the existing gift wrap item
          const updates = {};
          updates[this.giftWrapVariantId] = 0;

          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
          });

          // Build visible properties using translated labels (no underscore prefix = visible in checkout)
          const properties = {};
          if (recipientName) {
            properties[this.labelRecipientName] = recipientName;
          }
          if (recipientPhone) {
            properties[this.labelRecipientPhone] = recipientPhone;
          }
          if (giftMessage) {
            properties[this.labelGiftMessage] = giftMessage;
          }
          // Keep the hidden identifier
          properties['_gift_wrap'] = 'true';

          // Re-add with properties
          await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: parseInt(this.giftWrapVariantId, 10),
                quantity: 1,
                properties: properties
              }]
            }),
          });

        } catch (error) {
          console.error('Error updating gift wrap properties:', error);
        }
      }

      updateFieldsVisibility() {
        if (!this.fieldsContainer) return;

        const isChecked = this.toggleCheckbox.checked;

        if (isChecked) {
          this.fieldsContainer.hidden = false;
          this.fieldsContainer.setAttribute('aria-hidden', 'false');
          // Enable all inputs in the fields section
          this.enableFields();
        } else {
          this.fieldsContainer.hidden = true;
          this.fieldsContainer.setAttribute('aria-hidden', 'true');
          // Disable inputs to prevent form submission
          this.disableFields();
        }
      }

      updateAriaAttributes() {
        if (!this.fieldsContainer) return;

        const isChecked = this.toggleCheckbox.checked;
        this.toggleCheckbox.setAttribute('aria-expanded', isChecked.toString());
      }

      enableFields() {
        const inputs = this.getFieldInputs();
        inputs.forEach(input => {
          if (input) input.disabled = false;
        });
        // Also enable the hidden inputs for form submission
        const hiddenInputs = this.fieldsContainer.querySelectorAll('input[type="hidden"]');
        hiddenInputs.forEach(input => input.disabled = false);
      }

      disableFields() {
        const inputs = this.getFieldInputs();
        inputs.forEach(input => {
          if (input) input.disabled = true;
        });
        // Also disable the hidden inputs
        const hiddenInputs = this.fieldsContainer?.querySelectorAll('input[type="hidden"]');
        hiddenInputs?.forEach(input => input.disabled = true);
      }

      getFieldInputs() {
        return [
          this.giftWrapCheckbox,
          this.recipientNameInput,
          this.recipientPhoneInput,
          this.giftMessageTextarea
        ].filter(Boolean);
      }

      clearFields() {
        if (this.giftWrapCheckbox) this.giftWrapCheckbox.checked = false;
        if (this.recipientNameInput) this.recipientNameInput.value = '';
        if (this.recipientPhoneInput) this.recipientPhoneInput.value = '';
        if (this.giftMessageTextarea) {
          this.giftMessageTextarea.value = '';
          this.updateCharacterCounter();
        }
      }

      /**
       * Save gift options to cart attributes via AJAX
       */
      async saveCartAttributes() {
        if (this.isSavingAttributes) return;
        this.isSavingAttributes = true;

        try {
          const attributes = {
            'Is Gift': this.toggleCheckbox?.checked ? 'Yes' : '',
            'Recipient Name': this.recipientNameInput?.value || '',
            'Recipient Phone': this.recipientPhoneInput?.value || '',
            'Gift Message': this.giftMessageTextarea?.value || '',
            'Gift Wrap': this.giftWrapCheckbox?.checked ? 'Yes' : ''
          };

          const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ attributes }),
          });

          if (!response.ok) {
            throw new Error('Failed to save gift options');
          }

        } catch (error) {
          console.error('Error saving gift options:', error);
        } finally {
          this.isSavingAttributes = false;
        }
      }

      /**
       * Clear all gift-related cart attributes and order note
       */
      async clearCartAttributes() {
        try {
          const attributes = {
            'Is Gift': '',
            'Recipient Name': '',
            'Recipient Phone': '',
            'Gift Message': '',
            'Gift Wrap': ''
          };

          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ attributes, note: '' }),
          });

        } catch (error) {
          console.error('Error clearing gift options:', error);
        }
      }

      updateCharacterCounter() {
        if (!this.giftMessageTextarea || !this.characterCounter) return;

        const currentLength = this.giftMessageTextarea.value.length;
        const remaining = this.maxChars - currentLength;

        // Update the counter text
        this.characterCounter.textContent = `${remaining} characters remaining`;

        // Add warning/error classes based on remaining characters
        this.characterCounter.classList.remove('warning', 'error');
        if (remaining <= 0) {
          this.characterCounter.classList.add('error');
        } else if (remaining <= 20) {
          this.characterCounter.classList.add('warning');
        }
      }

      /**
       * Add the gift wrap product to cart
       */
      async addGiftWrapToCart() {
        if (this.isUpdating || !this.giftWrapVariantId) return;

        this.isUpdating = true;
        this.giftWrapCheckbox.disabled = true;

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: parseInt(this.giftWrapVariantId, 10),
                quantity: 1,
                properties: {
                  '_gift_wrap': 'true' // Hidden property to identify this item
                }
              }]
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to add gift wrap to cart');
          }

          // Publish cart update event if available
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-options-cart' });
          }

          // Refresh cart sections
          this.refreshCartSections();

        } catch (error) {
          console.error('Error adding gift wrap:', error);
          // Revert checkbox state on error
          this.giftWrapCheckbox.checked = false;
        } finally {
          this.isUpdating = false;
          this.giftWrapCheckbox.disabled = false;
        }
      }

      /**
       * Remove the gift wrap product from cart
       */
      async removeGiftWrapFromCart() {
        if (this.isUpdating || !this.giftWrapVariantId) return;

        this.isUpdating = true;
        this.giftWrapCheckbox.disabled = true;

        try {
          // Use cart/update.js with quantity 0 to remove the item
          const updates = {};
          updates[this.giftWrapVariantId] = 0;

          const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates }),
          });

          if (!response.ok) {
            throw new Error('Failed to remove gift wrap from cart');
          }

          // Publish cart update event if available
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-options-cart' });
          }

          // Refresh cart sections
          this.refreshCartSections();

        } catch (error) {
          console.error('Error removing gift wrap:', error);
          // Revert checkbox state on error
          this.giftWrapCheckbox.checked = true;
        } finally {
          this.isUpdating = false;
          this.giftWrapCheckbox.disabled = false;
        }
      }

      /**
       * Sync gift wrap checkbox state from cart contents
       */
      async syncGiftWrapFromCart() {
        if (!this.giftWrapCheckbox) return;

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart.js');
          if (!response.ok) return;

          const cart = await response.json();

          // Check if gift wrap product is in cart
          const hasGiftWrapProduct = this.giftWrapVariantId && cart.items.some(
            item => item.variant_id.toString() === this.giftWrapVariantId
          );

          // Check if cart has any real items (not just gift wrap)
          const hasRealItems = cart.items.some(
            item => !this.giftWrapVariantId || item.variant_id.toString() !== this.giftWrapVariantId
          );

          // If cart is empty (no real items), clear all gift options
          if (!hasRealItems || cart.item_count === 0) {
            if (this.toggleCheckbox?.checked) {
              this.toggleCheckbox.checked = false;
              this.updateFieldsVisibility();
            }
            if (this.giftWrapCheckbox?.checked) {
              this.giftWrapCheckbox.checked = false;
            }
            this.clearFields();
            this.clearCartAttributes();
            return;
          }

          // If gift wrap checkbox is checked but product not in cart, uncheck and clear attribute
          if (this.giftWrapCheckbox.checked && !hasGiftWrapProduct) {
            this.giftWrapCheckbox.checked = false;
            // Update just the Gift Wrap attribute
            await fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: { 'Gift Wrap': '' } }),
            });
          }

          // If product is in cart but checkbox not checked, check it
          if (!this.giftWrapCheckbox.checked && hasGiftWrapProduct) {
            this.giftWrapCheckbox.checked = true;
          }
        } catch (error) {
          console.error('Error syncing gift wrap state:', error);
        }
      }

      /**
       * Refresh cart sections after adding/removing gift wrap
       */
      refreshCartSections() {
        // Dispatch a custom event that cart components can listen for
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Update cart totals and icon without replacing the entire drawer
        this.updateCartTotals();
      }

      /**
       * Update cart totals and icon bubble without replacing the gift options component
       */
      async updateCartTotals() {
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`);
          if (!response.ok) return;

          const cart = await response.json();

          // Update cart icon bubble count
          const cartCountElements = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
          });

          // Update visually hidden count
          const cartCountHidden = document.querySelectorAll('.cart-count-bubble .visually-hidden');
          cartCountHidden.forEach(el => {
            el.textContent = `${cart.item_count} items`;
          });

          // Show/hide cart count bubble
          const cartBubbles = document.querySelectorAll('.cart-count-bubble');
          cartBubbles.forEach(bubble => {
            if (cart.item_count > 0) {
              bubble.classList.remove('hidden');
            } else {
              bubble.classList.add('hidden');
            }
          });

          // Update totals in cart drawer footer
          const totalElements = document.querySelectorAll('.totals__total-value');
          totalElements.forEach(el => {
            el.textContent = this.formatMoney(cart.total_price, cart.currency);
          });

          // Update cart items display in drawer (just the items table, not gift options)
          this.updateCartItemsDisplay(cart);

        } catch (error) {
          console.error('Error updating cart totals:', error);
        }
      }

      /**
       * Format money in the store's currency
       * Shopify returns prices in cents (hundredths), so divide by 100
       * KWD displays with 3 decimal places
       */
      formatMoney(cents, currency = 'KWD') {
        const amount = (cents / 100).toFixed(3);
        return `${amount} ${currency}`;
      }

      /**
       * Update cart items display without touching gift options
       */
      async updateCartItemsDisplay(cart) {
        // Find the cart items wrapper
        const cartItemsWrapper = document.querySelector('.drawer__cart-items-wrapper');
        if (!cartItemsWrapper) return;

        // Check if gift wrap item is in cart and update the display
        const giftWrapItem = cart.items.find(item =>
          item.variant_id.toString() === this.giftWrapVariantId
        );

        // If gift wrap is in cart, ensure checkbox is checked
        if (this.giftWrapCheckbox && giftWrapItem && !this.giftWrapCheckbox.checked) {
          this.giftWrapCheckbox.checked = true;
        }
      }
    }
  );
}
